import { useEffect, useState } from 'react';
import { useAuthStore } from '../../stores/auth';
import { useModalStore } from '../../stores/modal';
import { useToastStore } from '../../stores/toast';
import { Site, extractApiError } from '../../lib/organizations';
import { commitSubEntities, type ContactConflict } from '../../lib/site-creation-commit';
import { createSiteContact } from '../../lib/tbm';
import { MemberPhoneConflictModal } from '../sites/MemberPhoneConflictModal';
import { EditModalFooter } from '../common/EditModalFooter';
import Modal from '../Modal';
import { useDirtySnapshot } from '../../hooks/useDirtySnapshot';
import { useSiteFormState } from '../../hooks/useSiteFormState';
import { useSiteCreationState } from '../../hooks/useSiteCreationState';
import {
  useSiteCreationStepper,
  type SiteCreationStep,
} from '../../hooks/useSiteCreationStepper';
import { SiteFormBody } from '../sites/SiteFormBody';
import { SiteFormStepper } from '../sites/SiteFormStepper';
import { SiteFormStep2 } from '../sites/SiteFormStep2';
import { SiteFormStep3 } from '../sites/SiteFormStep3';
import { SiteFormStepConstruction } from '../sites/SiteFormStepConstruction';
import { StepFooter } from '../sites/StepFooter';

interface SiteFormModalProps {
  onSuccess?: (site: Site) => void;
}

const STEP_TITLE: Record<SiteCreationStep, string> = {
  step1: '새 현장 추가',
  construction: '건설 현장 정보',
  agency: '안전관리전문기관 상세',
  contacts: '현장 담당자',
};

/**
 * 폼이 있는 두 자리의 `id` — 푸터의 제출 버튼이 `form` 속성으로 가리킨다. base Modal이 본문과
 * 푸터를 형제 div로 렌더하므로 이 연결 없이는 `type="submit"`이 아무 폼에도 속하지 않는다.
 * 생성 모드의 2~4단계는 폼이 없어(버튼 onClick만) 여기 없다.
 */
const FORM_ID = {
  edit: 'site-edit-form',
  createStep1: 'site-create-step1-form',
} as const;

const STEP_LABEL: Record<SiteCreationStep, string> = {
  step1: '현장 정보',
  construction: '건설 정보',
  agency: '기관 상세',
  contacts: '담당자',
};

/**
 * 수정 모드 푸터 — 취소가 base Modal의 requestClose로 수렴해야 dirty 닫기 확인을 탄다.
 * (requestClose context는 Modal children/footer의 **하위 컴포넌트**에서만 잡히므로 푸터를
 * 분리했다. onClose를 직접 부르면 확인을 우회한다 — base Modal 규약)
 *
 * `submitForm`이 필수다 — base가 본문과 푸터를 **형제 div**로 렌더하므로 이 푸터는 본문의
 * `<form>` 밖이고, 그 연결이 없으면 `submitType="submit"`이 조용히 아무 일도 하지 않는다.
 *
 * 저장 게이트 + 이유 표시는 수정 모달 dirty 표준(hasChanges + "변경사항 있습니다/없음" —
 * 결정 `2026-08-16-edit-modal-dirty-gate-standard`).
 */
function SiteEditFooter({
  isDirty,
  isSaving,
  canSubmit,
  onFallbackClose,
}: {
  isDirty: boolean;
  isSaving: boolean;
  canSubmit: boolean;
  onFallbackClose: () => void;
}) {
  return (
    <EditModalFooter
      isDirty={isDirty}
      submitLabel="저장"
      submitType="submit"
      submitForm={FORM_ID.edit}
      submitDisabled={isSaving || !canSubmit || !isDirty}
      busy={isSaving}
      onFallbackClose={onFallbackClose}
    />
  );
}

export default function SiteFormModal({ onSuccess }: SiteFormModalProps) {
  const { currentMembership } = useAuthStore();
  const { showToast } = useToastStore();
  const {
    createSite,
    createSiteSource,
    editSite,
    closeCreateSite,
    closeEditSite,
    openCreateInspection,
  } = useModalStore();
  const companyId = currentMembership?.companyId;
  const organizationCategory = currentMembership?.company?.category;

  const isOpen = createSite || !!editSite;
  const isEditMode = !!editSite;

  const [isCommitting, setIsCommitting] = useState(false);
  /**
   * 담당자 인원 중복 대기열 — 현장은 이미 만들어졌고 이 건들만 사용자 선택이 필요하다.
   * 큐가 비어야 생성 흐름이 끝난다(모달 닫기). 여러 명이 겹칠 수 있어 배열로 순차 처리.
   */
  const [conflictQueue, setConflictQueue] = useState<ContactConflict[]>([]);
  const [committedSite, setCommittedSite] = useState<Site | null>(null);
  /** 충돌 1건 처리(담당자 생성 요청) 진행 중 — 더블클릭으로 같은 담당자가 2건 생기는 걸 막는다. */
  const [isResolvingConflict, setIsResolvingConflict] = useState(false);

  const creationState = useSiteCreationState();

  const state = useSiteFormState({
    companyId,
    isEdit: isEditMode,
    siteId: editSite ?? undefined,
    onSaved: async (site) => {
      if (isEditMode) {
        onSuccess?.(site);
        closeAndReset();
        return;
      }
      // 생성 모드 — 원자적 commit: Site 생성 후 sub-entities 순차 저장.
      // 진입 경로(createSiteSource)와 무관하게 저장한다. 점검에서 들어온 경우도 담당자 단계를
      // 똑같이 보여주므로, 여기서 갈라 현장만 만들고 닫으면 입력한 담당자가 전송조차 되지 않는다.
      // 인원 중복이 남았으면 여기서 끝내지 않는다 — 충돌 모달로 해결한 뒤 그쪽에서 마친다.
      const done = await runCommitSubEntities(site);
      if (!done) return;
      finishCreation(site);
    },
    // P4 — 생성이 «업체 승인 대기»로 끝났다. 현장이 없으니 sub-entity를 저장할 자리도, onSuccess로 알릴 현장도 없다.
    // 안내(훅이 띄운다)로 끝내고 닫는다 — 2~4단계에 입력한 값이 있으면 저장되지 않았음을 따로 알린다.
    onRequested: () => {
      if (creationState.isDirty) {
        showToast(
          '현장이 아직 만들어지지 않아 담당자·추가 정보는 저장되지 않았습니다. 승인된 뒤 현장 상세에서 입력해주세요.',
          'warning',
        );
      }
      closeAndReset();
    },
  });

  const stepper = useSiteCreationStepper({
    organizationCategory: organizationCategory ?? undefined,
    isConstructionIndustry: state.isConstructionIndustry,
  });

  // 수정 모드 dirty 게이트 — 로드된 값(formSnapshot)과 현재 값의 스냅샷 비교.
  // 생성 모드는 기존 "입력 존재 confirm"(handleClose) 유지라 게이트 비대상(enabled=false).
  const dirty = useDirtySnapshot(() => state.formSnapshot, {
    enabled: isEditMode,
  });
  const { capture: captureDirtyBaseline } = dirty;
  // 기준점은 **로드 완료 시점**의 값 — isFetching이 내려올 때마다 재캡처한다(다른 현장으로
  // 다시 열리는 경우 포함). 로드 전 임시 캡처는 무해하다(스피너 동안 상호작용 불가 + 완료 시 재캡처).
  useEffect(() => {
    if (isEditMode && !state.isFetching) {
      captureDirtyBaseline();
    }
  }, [isEditMode, state.isFetching, captureDirtyBaseline]);

  /**
   * 충돌 큐는 **현장 1건의 생성 흐름에만** 속한다. 남겨두면 다음 "새 현장 추가"에서 이전 현장의
   * 충돌 모달이 그대로 뜨고, 등록하면 담당자가 **이전 현장(committedSite)** 에 저장된다.
   * 그래서 모달이 닫히는 경로와 다시 열리는 경로 양쪽에서 반드시 비운다.
   */
  const resetConflictState = () => {
    setConflictQueue([]);
    setCommittedSite(null);
    setIsResolvingConflict(false);
  };

  useEffect(() => {
    if (createSite && !editSite) {
      state.resetForm();
      stepper.reset();
      creationState.reset();
      setIsCommitting(false);
      resetConflictState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSite]);

  const closeAndReset = () => {
    state.resetForm();
    stepper.reset();
    creationState.reset();
    setIsCommitting(false);
    resetConflictState();
    if (isEditMode) {
      closeEditSite();
    } else {
      closeCreateSite();
    }
  };

  /**
   * 생성 흐름의 유일한 종료 지점 — 성공 통지 + 정리, 그리고 점검에서 들어왔다면 점검 모달 재오픈.
   *
   * **sub-entity(담당자·공정·통계) 저장이 끝난 뒤에만** 부른다. 종전에는 점검 경로가 현장 생성
   * 직후 여기 해당하는 일을 직접 해 버려서, 담당자 단계에서 입력한 값이 서버로 전송조차 되지
   * 않고 사라졌다(2026-08-11 실사고 — 실패가 아니라 미시도라 에러도 로그도 남지 않았다).
   * 종료 처리가 두 곳으로 갈라져 있던 게 원인이라 한 곳으로 모은다.
   *
   * source는 `closeAndReset()`이 store를 비우기 전에 읽어둔다.
   */
  const finishCreation = (site: Site | null) => {
    const backToInspection = createSiteSource === 'inspection';
    // site가 null인 건 충돌 처리 도중 현장 참조를 잃은 예외 경로다. 알릴 현장이 없으니
    // onSuccess는 건너뛰되 정리와 점검 복귀는 정상 경로와 똑같이 해준다.
    if (site) onSuccess?.(site);
    closeAndReset();
    if (backToInspection) setTimeout(() => openCreateInspection(), 0);
  };

  /**
   * sub-entity 저장. 반환값 false = **아직 끝나지 않았다**(인원 중복 해결 대기) — 호출부는
   * 모달을 닫지 말아야 한다. 종전에는 담당자 409를 실패 문자열로 뭉쳐 info 토스트로 흘리고
   * 모달을 닫아, 사용자는 "담당자가 그냥 저장 안 됐다"로만 알고 해결할 길이 없었다(2026-08-11).
   */
  const runCommitSubEntities = async (site: Site): Promise<boolean> => {
    if (!companyId) return true;
    const { failures, contactConflicts } = await commitSubEntities(site, {
      companyId,
      creationState: {
        construction: creationState.construction,
        products: creationState.products,
        contacts: creationState.contacts,
        workerStats: creationState.workerStats,
        accidentStats: creationState.accidentStats,
        processTypes: creationState.processTypes,
      },
    });
    if (failures.length) {
      // 저장이 안 된 사실은 정보가 아니라 오류다 — info 톤이면 스쳐 지나간다.
      showToast(
        `현장은 생성되었으나 다음 정보 저장에 실패했습니다: ${failures.join(', ')}. 현장 상세에서 이어 입력해주세요.`,
        'error',
      );
    }
    if (contactConflicts.length > 0) {
      setCommittedSite(site);
      setConflictQueue(contactConflicts);
      return false;
    }
    return true;
  };

  /**
   * 인원 중복 1건 처리 — memberId를 주면 기존 인원에 연결하고, null이면 그 담당자를 건너뛴다.
   * 큐가 비면 그때 비로소 생성 흐름을 마친다(모달 닫기·onSuccess).
   */
  const resolveContactConflict = async (
    memberId: string | null,
    options: { asDifferentPerson?: boolean } = {},
  ) => {
    // 진행 중 재진입 차단 — 버튼 disabled만으로는 첫 클릭 직후의 두 번째 클릭을 놓친다.
    if (isResolvingConflict) return;
    const current = conflictQueue[0];
    const site = committedSite;
    if (!current) return;
    // 진행 불가 상태. 큐가 남은 채로 돌아가면 사용자가 갇힌다 — 큐가 있는 동안은 바깥 모달의
    // 닫기 수단이 전부 막혀 있어(preventClose) 빠져나갈 길이 없다. 여기서 흐름을 끝낸다.
    if (!site || !companyId) {
      showToast(
        '현장 정보를 잃어 담당자 등록을 이어갈 수 없습니다. 현장 상세에서 이어 입력해주세요.',
        'error',
      );
      // 현장은 이미 만들어졌다 — 담당자만 못 이은 것이라 흐름은 정상 종료와 같게 끝낸다.
      // closeAndReset만 부르면 점검에서 들어온 사용자는 점검 모달로 돌아가지 못하고 갇힌다.
      finishCreation(site ?? null);
      return;
    }
    if (memberId || options.asDifferentPerson) {
      setIsResolvingConflict(true);
      try {
        await createSiteContact(companyId, site.id, {
          name: current.pendingInput.name,
          phone: current.pendingInput.phone,
          department: current.pendingInput.department || undefined,
          position: current.pendingInput.position || undefined,
          // 이메일만 겹친 경우 "다른 사람"으로 그대로 등록 — 기존 인원에 연결하지 않는다
          ...(options.asDifferentPerson
            ? { email: current.pendingInput.email || undefined, allowDuplicateEmail: true }
            : { memberId: memberId! }),
        });
      } catch (err) {
        console.error('담당자 연결 실패', extractApiError(err));
        showToast(
          `'${current.pendingInput.name}' 담당자 연결에 실패했습니다. 현장 상세에서 이어 입력해주세요.`,
          'error',
        );
      } finally {
        setIsResolvingConflict(false);
      }
    }
    const rest = conflictQueue.slice(1);
    setConflictQueue(rest);
    if (rest.length === 0) {
      finishCreation(site);
    }
  };

  const handleClose = () => {
    // 중간 Step이거나 creation state에 입력이 있으면 확인
    const hasPendingInput =
      !isEditMode &&
      (creationState.isDirty ||
        state.name.trim().length > 0 ||
        state.registrationNumber.trim().length > 0 ||
        state.companyName.trim().length > 0);
    if (hasPendingInput) {
      const confirmed = window.confirm(
        '입력한 내용이 저장되지 않습니다. 닫으시겠습니까?',
      );
      if (!confirmed) return;
    }
    closeAndReset();
  };

  const handleNextFromStep1 = (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.canSubmit) return;
    stepper.goNext();
  };

  const handleCommit = async () => {
    if (!state.canSubmit || state.isLoading) return;
    setIsCommitting(true);
    await state.performSave(false);
    setIsCommitting(false);
  };

  // === 수정 모드: 기존 구조 유지 + dirty 게이트(무변경 저장 차단·미저장 닫기 보호) ===
  if (isEditMode) {
    const handleEditSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      // 버튼 disabled 우회 경로(입력 Enter 등)까지 무변경 저장을 막는다.
      if (!dirty.isDirty) return;
      void state.performSave(false);
    };
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="현장 수정"
        description="현장 정보를 수정합니다."
        size="xl"
        preventClose={state.isLoading}
        isDirty={dirty.isDirty}
        // 로드 중에는 푸터를 띄우지 않는다 — 종전에 푸터가 `<form>` 안(else 분기)에 있어
        // 스피너 동안 렌더되지 않았고, 그 성질을 유지한다(그냥 넘기면 저장 버튼이 새로 나타난다).
        footer={
          state.isFetching ? null : (
            <SiteEditFooter
              isDirty={dirty.isDirty}
              isSaving={state.isLoading}
              canSubmit={state.canSubmit}
              onFallbackClose={handleClose}
            />
          )
        }
      >
        {state.isFetching ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          </div>
        ) : (
          <form id={FORM_ID.edit} onSubmit={handleEditSubmit}>
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              <SiteFormBody state={state} isEdit compact />
            </div>
          </form>
        )}
      </Modal>
    );
  }

  // === 생성 모드: Stepper 기반 원자적 저장 ===
  const stepperSteps = stepper.visibleSteps.map((key) => ({
    key,
    label: STEP_LABEL[key],
    optional: key !== 'step1',
  }));

  const { step, isFirstStep } = stepper;
  const title = STEP_TITLE[step];

  /**
   * 단계별 푸터 — base `footer`는 모달당 **하나**라 단계 분기를 여기서 접는다(아래 본문 분기와
   * 짝이고 순서도 같게 둔다). 1단계만 `<form>`이라 `nextForm`으로 제출을 잇고, 나머지는
   * 버튼 onClick이라 그 prop이 없다.
   */
  const renderStepFooter = () => {
    if (step === 'step1') {
      return (
        <StepFooter
          onCancel={handleClose}
          cancelDisabled={state.isLoading}
          nextType="submit"
          nextForm={FORM_ID.createStep1}
          nextDisabled={state.isLoading || !state.canSubmit}
        />
      );
    }
    if (step === 'contacts') {
      return (
        <StepFooter
          onBack={() => stepper.goBack()}
          backDisabled={isFirstStep}
          onNext={handleCommit}
          nextDisabled={state.isLoading || isCommitting || !state.canSubmit}
          nextLabel={
            state.isLoading || isCommitting ? '저장 중...' : '저장하고 종료'
          }
        />
      );
    }
    return (
      <StepFooter
        onBack={() => stepper.goBack()}
        backDisabled={isFirstStep}
        onNext={() => stepper.goNext()}
      />
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      description={step === 'step1' ? '새로운 현장을 등록합니다.' : undefined}
      size="xl"
      // 충돌 모달이 떠 있는 동안 바깥 모달의 닫기 수단을 전부 끈다. base Modal의 Esc 핸들러는
      // document 레벨이고 stopPropagation이 없어, 두 모달이 함께 열려 있으면 Esc 한 번에 둘 다
      // 반응한다 — 그러면 충돌 큐가 남은 채 이 모달이 닫혀 다음 현장 생성으로 샌다.
      preventClose={state.isLoading || isCommitting || conflictQueue.length > 0}
      footer={renderStepFooter()}
    >
      <SiteFormStepper steps={stepperSteps} currentIndex={stepper.stepIndex} />

      {step === 'step1' && (
        <form id={FORM_ID.createStep1} onSubmit={handleNextFromStep1}>
          <div className="max-h-[60vh] overflow-y-auto pr-2">
            <SiteFormBody state={state} isEdit={false} compact />
          </div>
        </form>
      )}

      {step === 'construction' && (
        <>
          <div className="max-h-[60vh] overflow-y-auto pr-2">
            <SiteFormStepConstruction
              value={creationState.construction}
              onChange={creationState.setConstruction}
            />
          </div>
        </>
      )}

      {step === 'agency' && (
        <>
          <div className="max-h-[60vh] overflow-y-auto pr-2">
            <SiteFormStep2
              workerStatsCurrent={creationState.workerStats.current}
              workerStatsPrevious={creationState.workerStats.previous}
              onWorkerStatsCurrentChange={creationState.setWorkerStatsCurrent}
              onWorkerStatsPreviousChange={creationState.setWorkerStatsPrevious}
              accidentStatsCurrent={creationState.accidentStats.current}
              accidentStatsPrevious={creationState.accidentStats.previous}
              onAccidentStatsCurrentChange={
                creationState.setAccidentStatsCurrent
              }
              onAccidentStatsPreviousChange={
                creationState.setAccidentStatsPrevious
              }
              processTypes={creationState.processTypes}
              onAddProcessType={creationState.addProcessType}
              onRemoveProcessType={creationState.removeProcessType}
              onMoveProcessType={creationState.moveProcessType}
              products={creationState.products}
              onProductsChange={creationState.setProducts}
            />
          </div>
        </>
      )}

      {step === 'contacts' && (
        <>
          <div className="max-h-[60vh] overflow-y-auto pr-2">
            <SiteFormStep3
              contacts={creationState.contacts}
              onAddContact={creationState.addContact}
              onRemoveContact={creationState.removeContact}
            />
          </div>
        </>
      )}

      {/* 인원 중복 해결 — 현장 상세 경로(SiteContactFormModal)와 **같은 모달·같은 선택지**다.
          "기존 인원으로 추가"는 memberId 경로라 같은 사람의 Member가 하나 더 생기지 않는다.
          z-[80] sub 모달이라 이 폼 위에 뜬다(모달 z-index 정책). */}
      {conflictQueue.length > 0 && (
        <MemberPhoneConflictModal
          isOpen
          existingMember={conflictQueue[0].existingMember}
          pendingInput={conflictQueue[0].pendingInput}
          matchedBy={conflictQueue[0].matchedBy}
          isProcessing={isResolvingConflict}
          onSelectExisting={(memberId) => void resolveContactConflict(memberId)}
          onCreateAsDifferentPerson={() =>
            void resolveContactConflict(null, { asDifferentPerson: true })
          }
          onCancel={() => void resolveContactConflict(null)}
        />
      )}
    </Modal>
  );
}
